import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
// eslint-disable-next-line import/no-unresolved
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS, ALGOLIA_PRODUCT_INDEXES } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import useScreen from 'hooks/useScreen';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import CustomerNameWithEmoji from 'components/consumer/CustomerNameWithEmoji';
import { BASE_ATTRIBUTES_TO_RETRIEVE, appendInStockWarehouseAttributeIfZip } from 'components/consumer/category-page-algolia/categoryPageTypes';
import useWarehouseIdForUser from 'global-state/warehouseIdForUser/useWarehouseIdForUser';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import ProductListingPageDescriptionUI from '../ProductListingPageDescriptionUI';
import AdvancedSearchSidebar from '../AdvancedSearchSidebar';
import AdvancedSearchProductHits from '../AdvancedSearchProductHits';
import AdvancedSearchSecondaryIndexes from '../AdvancedSearchSecondaryIndexes';
import useAdvancedSearchContext from '../../hooks/useAdvancedSearchContext';
import useAdvancedSearchURL from '../../hooks/useAdvancedSearchURL';
import DesktopProductListingColorFilter from './components/DesktopProductListingColorFilter';
import ProductListingSortBy from './components/ProductListingSortBy';
import MobileProductFilters from './components/MobileProductFilters';
import buildSearchAlgoliaConfigureFilters from '../../buildSearchAlgoliaConfigureFilters';

// Helper component to sync query from parent to nested Index
const VirtualSearchBox = t0 => {
  const $ = _c(5);
  const {
    parentQuery
  } = t0;
  const {
    query,
    refine
  } = useSearchBox();
  let t1;
  let t2;
  if ($[0] !== parentQuery || $[1] !== query || $[2] !== refine) {
    t1 = () => {
      if (parentQuery && parentQuery !== query) {
        refine(parentQuery);
      }
    };
    t2 = [parentQuery, query, refine];
    $[0] = parentQuery;
    $[1] = query;
    $[2] = refine;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
};
const SectionResultsCountTracker = t0 => {
  const $ = _c(6);
  const {
    onCountChange
  } = t0;
  const {
    results
  } = useInstantSearch();
  let t1;
  if ($[0] !== onCountChange || $[1] !== results?.nbHits) {
    t1 = () => {
      const count = results?.nbHits;
      if (Number.isFinite(count)) {
        onCountChange(count);
      }
    };
    $[0] = onCountChange;
    $[1] = results?.nbHits;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = results?.nbHits;
  let t3;
  if ($[3] !== onCountChange || $[4] !== t2) {
    t3 = [t2, onCountChange];
    $[3] = onCountChange;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t1, t3);
  return null;
};
const AlgoliaHits = t0 => {
  const $ = _c(86);
  const {
    loading: t1,
    pageName: t2,
    variant: t3,
    isFinalProductCountKnown: t4,
    isDesktopSidebarOpen: t5,
    pageLongDescription: t6
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const pageName = t2 === undefined ? "" : t2;
  const variant = t3 === undefined ? "advanced-search" : t3;
  const isFinalProductCountKnown = t4 === undefined ? true : t4;
  const isDesktopSidebarOpen = t5 === undefined ? true : t5;
  const pageLongDescription = t6 === undefined ? "" : t6;
  const {
    isMobile
  } = useScreen();
  const {
    currentSearchSection,
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  const {
    results,
    status
  } = useInstantSearch();
  const {
    query: parentQuery
  } = useSearchBox();
  const {
    queryUrlParam
  } = useAdvancedSearchURL();
  const warehouseId = useWarehouseIdForUser();
  const hasUserZip = !!useZipCodeFromUser();
  const [activeSectionCount, setActiveSectionCount] = useState(results?.nbHits || 0);
  const [delayedResultsCount, setDelayedResultsCount] = useState(null);
  const isSearchPending = status === "loading" || status === "stalled";
  const getCustomProps = _temp2;
  const [isSidebarOpen, setIsDesktopSidebarOpen] = useState(isDesktopSidebarOpen);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsDesktopSidebarOpen(_temp3);
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const toggleSidebar = t7;
  const searchTerm = parentQuery || results?.query || queryUrlParam || pageName || "";
  const displayText = searchTerm ? `"${searchTerm}"` : "\"\"";
  const productsCount = results?.nbHits || 0;
  let t8;
  if ($[1] !== currentProductIndex || $[2] !== currentSearchSection || $[3] !== results) {
    t8 = () => {
      if (currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && Number.isFinite(results?.nbHits)) {
        setActiveSectionCount(results.nbHits);
      }
    };
    $[1] = currentProductIndex;
    $[2] = currentSearchSection;
    $[3] = results;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const t9 = results?.nbHits;
  let t10;
  if ($[5] !== currentProductIndex || $[6] !== currentSearchSection || $[7] !== t9) {
    t10 = [currentSearchSection, currentProductIndex, t9];
    $[5] = currentProductIndex;
    $[6] = currentSearchSection;
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  useEffect(t8, t10);
  let t11;
  let t12;
  if ($[9] !== activeSectionCount || $[10] !== isFinalProductCountKnown || $[11] !== isSearchPending) {
    t11 = () => {
      if (isSearchPending || !isFinalProductCountKnown) {
        setDelayedResultsCount(null);
        return;
      }
      if (activeSectionCount > 0) {
        const timer = setTimeout(() => {
          setDelayedResultsCount(activeSectionCount);
        }, 750);
        return () => clearTimeout(timer);
      }
      setDelayedResultsCount(null);
    };
    t12 = [activeSectionCount, isSearchPending, isFinalProductCountKnown, 750];
    $[9] = activeSectionCount;
    $[10] = isFinalProductCountKnown;
    $[11] = isSearchPending;
    $[12] = t11;
    $[13] = t12;
  } else {
    t11 = $[12];
    t12 = $[13];
  }
  useEffect(t11, t12);
  const resultsCountText = !loading && isFinalProductCountKnown && delayedResultsCount != null && delayedResultsCount > 0 ? `${delayedResultsCount} Results` : null;
  let t13;
  if ($[14] !== hasUserZip || $[15] !== isReadyToShipActive || $[16] !== warehouseId) {
    t13 = buildSearchAlgoliaConfigureFilters({
      isReadyToShipActive,
      warehouseId,
      hasUserZip
    });
    $[14] = hasUserZip;
    $[15] = isReadyToShipActive;
    $[16] = warehouseId;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const secondaryFilters = t13;
  let t14;
  if ($[18] !== hasUserZip || $[19] !== isReadyToShipActive) {
    t14 = appendInStockWarehouseAttributeIfZip(BASE_ATTRIBUTES_TO_RETRIEVE, hasUserZip, isReadyToShipActive);
    $[18] = hasUserZip;
    $[19] = isReadyToShipActive;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const productAttributesToRetrieve = t14;
  let filters;
  if ($[21] !== maxWidth || $[22] !== minWidth) {
    filters = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    $[21] = maxWidth;
    $[22] = minWidth;
    $[23] = filters;
  } else {
    filters = $[23];
  }
  const secondaryNumericFilters = filters;
  const t15 = variant === "advanced-search";
  const t16 = variant === "advanced-search";
  const t17 = !variant || variant !== "advanced-search";
  let t18;
  if ($[24] !== t16 || $[25] !== t17) {
    t18 = classNames("px-3 py-4 space-y-1", "md:px-[3.333vw] md:py-0 md:space-y-0 min-[1500px]:px-[50px]", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:mt-4", "pt-0 md:pt-0", {
      "md:top-[55px]": t16,
      "md:top-[117px]": t17
    });
    $[24] = t16;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== resultsCountText) {
    t19 = resultsCountText && <span className="text-sm text-gray-light2 font-normal ml-3">{resultsCountText}</span>;
    $[27] = resultsCountText;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== displayText || $[30] !== t19) {
    t20 = <>Results for {displayText}{t19}</>;
    $[29] = displayText;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = variant === "advanced-search";
  let t22;
  if ($[32] !== t20 || $[33] !== t21) {
    t22 = <div className="md:flex md:gap-2 md:items-center md:flex-nowrap min-w-0"><ProductListingPageName pageName={t20} showPageNameWhileLoading={t21} /></div>;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== loading) {
    t23 = <DesktopShowHideFiltersButton loading={loading} onClick={toggleSidebar} />;
    $[35] = loading;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t22 || $[38] !== t23) {
    t24 = <div className="md:flex md:gap-5 md:items-center min-w-0">{t22}{t23}</div>;
    $[37] = t22;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  let t26;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t26 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[40] = t25;
    $[41] = t26;
  } else {
    t25 = $[40];
    t26 = $[41];
  }
  let t27;
  if ($[42] !== currentSearchSection || $[43] !== isMobile || $[44] !== productsCount) {
    t27 = ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) && isMobile && <><MobileProductFilters productsCount={productsCount} /><div className="flex-1 min-w-[140px] flex items-center"><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp4)} /></div></>;
    $[42] = currentSearchSection;
    $[43] = isMobile;
    $[44] = productsCount;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  let t28;
  if ($[46] !== currentSearchSection || $[47] !== isMobile) {
    t28 = ALGOLIA_PRODUCT_INDEXES.includes(currentSearchSection) && !isMobile && <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp5)} /></div>;
    $[46] = currentSearchSection;
    $[47] = isMobile;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== t27 || $[50] !== t28) {
    t29 = <div className={t26}>{t27}{t28}</div>;
    $[49] = t27;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <MobileGridColumnToggle />;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t29) {
    t31 = <div className={t25}>{t29}{t30}</div>;
    $[53] = t29;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t18 || $[56] !== t24 || $[57] !== t31) {
    t32 = <div className={t18}>{t24}{t31}</div>;
    $[55] = t18;
    $[56] = t24;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== isSidebarOpen) {
    t33 = <AdvancedSearchSidebar isOpen={isSidebarOpen} />;
    $[59] = isSidebarOpen;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== currentProductIndex || $[62] !== currentSearchSection) {
    t34 = currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS && <><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><AdvancedSearchProductHits /></>;
    $[61] = currentProductIndex;
    $[62] = currentSearchSection;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  let t35;
  if ($[64] !== currentProductIndex || $[65] !== currentSearchSection || $[66] !== productAttributesToRetrieve || $[67] !== searchTerm || $[68] !== secondaryFilters || $[69] !== secondaryNumericFilters) {
    t35 = currentSearchSection === ALGOLIA_INDEX_NAME.PRODUCTS && currentProductIndex !== ALGOLIA_INDEX_NAME.PRODUCTS && <Index indexName={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={searchTerm} /><Configure facets={["*"]} filters={secondaryFilters} numericFilters={secondaryNumericFilters} attributesToRetrieve={productAttributesToRetrieve} clickAnalytics={true} /><SectionResultsCountTracker onCountChange={setActiveSectionCount} /><AdvancedSearchProductHits /></Index>;
    $[64] = currentProductIndex;
    $[65] = currentSearchSection;
    $[66] = productAttributesToRetrieve;
    $[67] = searchTerm;
    $[68] = secondaryFilters;
    $[69] = secondaryNumericFilters;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <AdvancedSearchSecondaryIndexes getCustomProps={getCustomProps} onCountChange={setActiveSectionCount} />;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== pageLongDescription || $[73] !== pageName) {
    t37 = <ProductListingPageDescriptionUI pageLongDescription={pageLongDescription} pageName={pageName} />;
    $[72] = pageLongDescription;
    $[73] = pageName;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t34 || $[76] !== t35 || $[77] !== t37) {
    t38 = <div className="md:space-y-8 md:mb-8 md:flex-1">{t34}{t35}{t36}{t37}</div>;
    $[75] = t34;
    $[76] = t35;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== t33 || $[80] !== t38) {
    t39 = <div className="md:flex md:flex-row md:gap md:mt-2 md:px-[3.333vw] min-[1500px]:px-[50px]">{t33}{t38}</div>;
    $[79] = t33;
    $[80] = t38;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t15 || $[83] !== t32 || $[84] !== t39) {
    t40 = <FixedWidthContainer wide={true} noMobilePadding={true} noPadding={t15}>{t32}{t39}</FixedWidthContainer>;
    $[82] = t15;
    $[83] = t32;
    $[84] = t39;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  return t40;
};
export default AlgoliaHits;
function _temp(p) {
  return p.upholstery;
}
function _temp2(selectedGridItem) {
  if (!selectedGridItem) {
    return {};
  }
  const customerName = `${selectedGridItem.customer_first_name || ""} ${selectedGridItem.customer_last_name && selectedGridItem.customer_last_name[0] ? `${selectedGridItem.customer_last_name[0]}.` : ""}`.trim();
  const products = selectedGridItem.products || [];
  return {
    imageProps: {
      src: transformInspirationImageUrl(selectedGridItem.image_link || selectedGridItem.photo_image || selectedGridItem.photo_url) || "",
      trim: "auto"
    },
    selectedItemName: <CustomerNameWithEmoji>{customerName}</CustomerNameWithEmoji>,
    selectedItemNameMobile: <span className="CustomerPhotosCustomerNameMobile">{customerName}</span>,
    selectedItemProducts: products,
    selectedItemMaterialList: products.map(_temp).filter(Boolean),
    selectedItemDescription: selectedGridItem.photo_comment || selectedGridItem.photo_description || "",
    showDescriptionQuoteMark: true
  };
}
function _temp3(prevIsOpen) {
  return !prevIsOpen;
}
function _temp4(option) {
  return option.value !== "newest";
}
function _temp5(option_0) {
  return option_0.value !== "newest";
}